import React from 'react';
import { QuestionnaireBlockProps } from './QuestionnaireBlock';
import { ReactComponent as File } from 'assets/icons/file.svg';
import { ReactComponent as Download } from 'assets/icons/file-download.svg';
import { isValidJson } from 'utilities/jsonHelpers';
import { FileOutput } from 'graphql/generated/hasura';
import {
  formatFileSize,
  mapFileTypeToApiFileType,
} from 'utilities/fileHelpers';
import { twMerge } from 'tailwind-merge';
import { useGetFileFromStorage } from 'hooks/useGetFileFromStorage';
import Spinner from 'components/Spinner';

const AdditionalInfo: React.FC<
  QuestionnaireBlockProps & {
    patientId?: string;
    className?: string;
    fileWrapperClassName?: string;
  }
> = ({
  questionnaire = [],
  title,
  patientId,
  className,
  fileWrapperClassName,
}) => {
  const [info] = questionnaire;

  const { fetchFileFromStorage, downloadURI, loading } =
    useGetFileFromStorage();
  const getFile = async (fileId?: string, fileName?: string) => {
    if (!fileId) {
      return;
    }
    const url = await fetchFileFromStorage(fileId, patientId);
    if (!url) {
      return null;
    }
    return downloadURI(url, fileName);
  };

  const isValidJsonInfo = isValidJson(info?.answer as string);

  if (!isValidJsonInfo) {
    return (
      <div
        className={twMerge('grid grid-cols-2 desktop:gap-30 gap-5', className)}
      >
        <div className="self-stretch col-span-full justify-start text-neutral-800 text-xl font-semibold leading-normal">
          {title}
        </div>
        <div className="col-span-full">Incorrect info</div>
      </div>
    );
  }

  const { detail, files = [] } = JSON.parse(info?.answer as string);

  return (
    <div
      className={twMerge('grid grid-cols-6 desktop:gap-30 gap-5', className)}
    >
      <div className="self-stretch col-span-full justify-start text-neutral-800 text-xl font-semibold leading-normal">
        {title}
      </div>
      <div
        className={twMerge(
          'desktop:col-span-3 col-span-full flex flex-col gap-2.5',
          !detail && 'hidden',
          !files.length && 'col-span-full',
        )}
      >
        <div className="self-stretch justify-start text-neutral-800 text-base font-semibold  leading-snug">
          {'Details'}
        </div>
        <div className="self-stretch justify-start text-med-gray-3 text-base font-medium leading-snug whitespace-pre-wrap break-words">
          {detail}
        </div>
      </div>
      <div
        className={twMerge(
          'desktop:col-span-2 col-span-full flex flex-col gap-2.5',
          !files.length && 'hidden',
          !detail && 'desktop:col-span-4',
        )}
      >
        <div className="self-stretch justify-start text-neutral-800 text-base font-semibold leading-snug">
          {'Files'}
        </div>
        <div
          className={twMerge(
            'grid grid-cols-1 desktop:grid-cols-2 gap-2.5',
            !detail && 'desktop:grid-cols-3',
            fileWrapperClassName,
          )}
        >
          {files.map((file: FileOutput) => (
            <div
              key={file?.fileId}
              onClick={() =>
                getFile(file?.fileId as string, file?.fileName as string)
              }
              className={twMerge(
                'w-full bg-white hover:bg-hover-blue transition-all duration-300 border cursor-pointer border-gray-opacity-15 hover:border-clc-blue rounded-5 flex items-center gap-2.5 p-2.5',
                loading && 'opacity-50 pointer-events-none',
              )}
            >
              <File className="w-5 h-5 min-w-[20px] fill-med-gray-3" />
              <div className="flex flex-col flex-1 min-w-0">
                <p className="font-semibold text-neutral-800 truncate">
                  {file.fileName}
                </p>
                <p className="font-semibold text-med-gray-3 text-sm">
                  {mapFileTypeToApiFileType[
                    file.contentType as string
                  ]?.toLowerCase()}{' '}
                  • {formatFileSize(Number(file.size) || 0)}
                </p>
              </div>
              {loading ? (
                <Spinner />
              ) : (
                <Download className="w-5 h-5 min-w-[20px] cursor-pointer fill-clc-blue" />
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AdditionalInfo;
